/**
 * Bulk CSV product upload — drag-and-drop / pick-file → validate → import.
 *
 * Two-phase flow:
 *   1. POST /api/admin/products/bulk-validate  → preview + errors
 *   2. POST /api/admin/products/bulk-import    → actually inserts
 *
 * The operator MUST resolve every validation error before the import button
 * unlocks. Phase 2 is all-or-nothing — no partial imports — so the product
 * numbering stays clean.
 */
import React, { useRef, useState } from "react";
import { Upload, X, FileText, CheckCircle2, AlertCircle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const CSV_TEMPLATE = [
  "id,name_en,name_ar,name_fr,description_en,category,is_shein,shein_category,color_en,price,discount_pct,sizes,quantity,images,authentic,available",
  ",Soft Cotton Tee,قميص قطن ناعم,T-shirt en coton doux,Breathable everyday cotton tee,clothing,false,,white,19.99,10,\"S,M,L\",50,https://example.com/img1.jpg|https://example.com/img2.jpg,true,true",
  ",Mini Crossbody Bag,حقيبة كروس صغيرة,Petit sac bandoulière,,shein,true,accessories,black,12.50,0,,30,https://example.com/bag.jpg,false,true",
].join("\n");

export default function BulkUploadModal({ open, onClose, apiFetch, onImported }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | validating | preview | importing | done
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setReport(null);
    setPhase("idle");
  };

  const onPick = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please pick a .csv file");
      return;
    }
    setFile(f);
    setReport(null);
    setPhase("validating");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await apiFetch("/admin/products/bulk-validate", { method: "POST", body: fd, headers: undefined });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Validation failed");
      setReport(data);
      setPhase("preview");
      if (data.error_count > 0) {
        toast.error(`Found ${data.error_count} error${data.error_count === 1 ? "" : "s"} — fix the CSV first`);
      } else {
        toast.success(`${data.valid_count} row${data.valid_count === 1 ? "" : "s"} ready to import`);
      }
    } catch (e) {
      toast.error(e?.message || "Could not parse CSV");
      setPhase("idle");
    } finally {
      setBusy(false);
    }
  };

  const onImport = async () => {
    if (!file) return;
    setPhase("importing");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/admin/products/bulk-import", { method: "POST", body: fd, headers: undefined });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail?.message || data?.detail || "Import failed");
      toast.success(`Imported ${data.imported_count} product${data.imported_count === 1 ? "" : "s"} (${data.skipped_count} skipped)`);
      setReport({ ...report, imported: data.imported, skipped: data.skipped, imported_count: data.imported_count, skipped_count: data.skipped_count });
      setPhase("done");
      if (typeof onImported === "function") onImported();
    } catch (e) {
      toast.error(e?.message || "Import failed");
      setPhase("preview");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bob-bulk-products-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="bulk-upload-modal">
      <div className="w-full max-w-3xl bg-[#141414] rounded-2xl border border-white/10 shadow-2xl my-4 sm:my-8 max-h-[calc(100vh-2rem)] flex flex-col">
        <header className="flex items-center justify-between p-4 sm:p-5 border-b border-white/5 shrink-0">
          <div>
            <h3 className="font-display text-base sm:text-lg font-bold">Bulk Product Upload</h3>
            <p className="text-[11px] text-white/40 mt-0.5">CSV — supports BOB Store and SHEIN products</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-[11px] font-bold" data-testid="bulk-download-template">
              <Download className="w-3.5 h-3.5" /> Template
            </button>
            <button type="button" onClick={() => { reset(); onClose(); }} className="text-white/40 hover:text-white p-1" data-testid="bulk-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-4">
          {/* File picker / dropzone */}
          {phase === "idle" && (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-white/15 rounded-2xl p-8 sm:p-12 text-center cursor-pointer hover:bg-white/[0.02]"
              data-testid="bulk-dropzone"
            >
              <Upload className="w-10 h-10 mx-auto text-white/40 mb-3" />
              <div className="text-sm font-semibold text-white/80">Drop a CSV here or click to choose</div>
              <div className="text-xs text-white/40 mt-1">
                Required: <span className="font-mono text-white/60">name_en, price, images</span>. Empty <span className="font-mono">id</span> = auto-numbered.
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onPick(e.target.files?.[0])}
                className="hidden"
                data-testid="bulk-file-input"
              />
            </div>
          )}

          {/* Validating spinner */}
          {phase === "validating" && (
            <div className="flex items-center gap-3 justify-center py-12 text-white/60">
              <Loader2 className="w-5 h-5 animate-spin" /> Validating CSV…
            </div>
          )}

          {/* Preview / errors */}
          {report && (phase === "preview" || phase === "importing" || phase === "done") && (
            <div className="space-y-3" data-testid="bulk-report">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Valid rows</div>
                  <div className="text-lg font-bold text-emerald-200">{report.valid_count}</div>
                </div>
                <div className={`rounded-lg p-2.5 border ${report.error_count > 0 ? "bg-red-500/10 border-red-500/30" : "bg-white/5 border-white/10"}`}>
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${report.error_count > 0 ? "text-red-300" : "text-white/40"}`}>Errors</div>
                  <div className={`text-lg font-bold ${report.error_count > 0 ? "text-red-200" : "text-white/40"}`}>{report.error_count}</div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Total seen</div>
                  <div className="text-lg font-bold text-white/80">{report.total_seen}</div>
                </div>
              </div>

              {/* Errors */}
              {report.error_count > 0 && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3" data-testid="bulk-errors">
                  <div className="flex items-center gap-1.5 text-red-300 font-bold text-xs uppercase tracking-wider mb-2">
                    <AlertCircle className="w-4 h-4" /> Fix these errors first
                  </div>
                  <ul className="space-y-1 text-xs">
                    {report.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className="bg-black/40 rounded-lg px-2 py-1.5 text-red-100">
                        <span className="font-mono text-[10px] text-red-400">row {e.row}</span>
                        {e.field && <span className="ms-2 font-mono text-[10px] text-red-300">[{e.field}]</span>}
                        <span className="ms-2">{e.message}</span>
                      </li>
                    ))}
                    {report.errors.length > 50 && (
                      <li className="text-red-300/60 italic">+{report.errors.length - 50} more…</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Preview rows (first 10) */}
              {report.rows && report.rows.length > 0 && phase !== "done" && (
                <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3" data-testid="bulk-preview">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-2">
                    Preview — first {Math.min(10, report.rows.length)} valid rows
                  </div>
                  <ul className="space-y-1 text-xs">
                    {report.rows.slice(0, 10).map((r) => (
                      <li key={r.row} className="bg-black/30 rounded-lg px-2 py-1.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] text-white/40">row {r.row}</span>
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${r.is_shein ? "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30" : "bg-[#F37021]/15 text-[#F37021] border-[#F37021]/30"}`}>
                          {r.is_shein ? "SHEIN" : "BOB"}
                        </span>
                        <span className="text-white/80 font-semibold truncate">{r.payload?.name?.en || "—"}</span>
                        <span className="text-white/40">${r.payload?.originalPrice || 0}</span>
                        {(r.payload?.images || []).length > 0 && <span className="text-[10px] text-white/40">{(r.payload.images || []).length} img</span>}
                      </li>
                    ))}
                    {report.rows.length > 10 && (
                      <li className="text-white/40 italic">+{report.rows.length - 10} more rows…</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Imported / skipped */}
              {phase === "done" && (
                <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-3 text-emerald-200 text-sm" data-testid="bulk-done">
                  <div className="flex items-center gap-1.5 text-emerald-300 font-bold text-xs uppercase tracking-wider mb-2">
                    <CheckCircle2 className="w-4 h-4" /> Import complete
                  </div>
                  <div className="text-xs">
                    Imported <b className="text-white">{report.imported_count}</b> product(s).
                    {report.skipped_count > 0 && <> Skipped <b className="text-yellow-200">{report.skipped_count}</b> (duplicate IDs).</>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 p-4 sm:p-5 border-t border-white/5 shrink-0">
          <div className="text-[11px] text-white/40 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {file ? file.name : "No file picked"}
          </div>
          <div className="flex items-center gap-2">
            {phase === "preview" && (
              <button type="button" onClick={reset} className="h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold" data-testid="bulk-restart">
                Pick another file
              </button>
            )}
            {phase === "done" && (
              <button type="button" onClick={() => { reset(); onClose(); }} className="h-9 px-4 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-bold">
                Close
              </button>
            )}
            {(phase === "preview" || phase === "importing") && (
              <button
                type="button"
                onClick={onImport}
                disabled={busy || report?.error_count > 0 || report?.valid_count === 0}
                className="h-9 px-4 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="bulk-import-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {report?.error_count > 0 ? "Fix errors first" : `Import ${report?.valid_count || 0} product(s)`}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
